import { ChevronRightIcon, PaperClipIcon } from "@heroicons/react/24/outline";
import { EmailEntityId, UserMailboxMessageId, relativeTimeAgo } from "@sp-crm/core";
import React from "react";
import { isoDateStringToHumanReadable } from "../../util/date/locale-date-format";
import { getSanitizedMarkUpFromReceivedEmailBody } from "../../util/email";
import { Spinner } from "../ui/spinner";
import { ReceivedMessage } from "./received-message-detail";
import { formatContactDisplay } from "./unified-message";
import { MessageBodyData, useReceivedMessageBodyQuery } from "./use-message-body";

interface Props {
    message: ReceivedMessage;
    entityId?: EmailEntityId;
    onCollapse: () => void;
    headerActions?: React.ReactNode;
}

const ReceivedMessageBody: React.FC<{
    message: ReceivedMessage;
    entityId?: EmailEntityId;
}> = ({ message, entityId }) => {
    const query = useReceivedMessageBodyQuery(
        entityId,
        message.id as UserMailboxMessageId,
    );

    if (query.isLoading) {
        return (
            <div className="pt-2 pb-3 pl-14 pr-4">
                <Spinner />
            </div>
        );
    }

    if (query.isError) {
        return (
            <div className="pt-2 pb-3 pl-14 pr-4 text-red-600 text-sm">
                Failed to load message body.
            </div>
        );
    }

    const body = query.data as MessageBodyData | undefined;

    let content: React.ReactNode = null;
    if (body?.html) {
        content = (
            <div
                className="unreset text-sm text-gray-700"
                dangerouslySetInnerHTML={getSanitizedMarkUpFromReceivedEmailBody(
                    body.html,
                )}
            />
        );
    } else if (body?.plain) {
        content = (
            <div className="text-sm text-gray-700 whitespace-pre-wrap">{body.plain}</div>
        );
    } else if (message.bodyPreview) {
        content = (
            <div className="text-sm text-gray-700 whitespace-pre-wrap">
                {message.bodyPreview}
            </div>
        );
    }

    return <div className="pt-2 pb-3 pl-14 pr-4">{content}</div>;
};

export const ReceivedMessageInlineDetail: React.FC<Props> = props => {
    const { message, entityId, onCollapse, headerActions } = props;

    const fromDisplay = formatContactDisplay(message.from?.name, message.from?.email);

    const toRecipients = message.to
        .map(r => formatContactDisplay(r.contact.name, r.contact.email))
        .join(", ");

    const ccRecipients = message.cc
        .map(r => formatContactDisplay(r.contact.name, r.contact.email))
        .join(", ");

    const bccRecipients = message.bcc
        .map(r => formatContactDisplay(r.contact.name, r.contact.email))
        .join(", ");

    const timestamp = new Date(message.receivedTimestamp);

    return (
        <div className="border-b border-gray-200">
            <div
                className="flex items-start gap-3 px-3 py-2 pl-8 hover:bg-gray-50 cursor-pointer"
                onClick={onCollapse}>
                <ChevronRightIcon
                    className="h-3 w-3 text-gray-400 flex-shrink-0 mt-1 rotate-90"
                    aria-hidden="true"
                />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-sm text-gray-900">
                            {fromDisplay}
                        </span>
                        <div className="flex items-center gap-2 flex-shrink-0">
                            {message.attachmentCount > 0 && (
                                <span className="inline-flex items-center gap-0.5 text-xs text-gray-500">
                                    <PaperClipIcon className="h-3.5 w-3.5 text-gray-400" />
                                    {message.attachmentCount}
                                </span>
                            )}
                            <span className="text-xs text-gray-500">
                                {relativeTimeAgo(timestamp)}
                            </span>
                            {headerActions}
                        </div>
                    </div>
                    <div className="text-sm text-gray-500">
                        <span className="text-gray-400">To:</span> {toRecipients}
                    </div>
                    {ccRecipients ? (
                        <div className="text-sm text-gray-500">
                            <span className="text-gray-400">Cc:</span> {ccRecipients}
                        </div>
                    ) : null}
                    {bccRecipients ? (
                        <div className="text-sm text-gray-500">
                            <span className="text-gray-400">Bcc:</span> {bccRecipients}
                        </div>
                    ) : null}
                    <div className="text-sm text-gray-500">
                        <span className="text-gray-400">Received:</span>{" "}
                        {isoDateStringToHumanReadable(message.receivedTimestamp)}
                    </div>
                </div>
            </div>
            <ReceivedMessageBody message={message} entityId={entityId} />
        </div>
    );
};
